import { useNavigate } from "@tanstack/solid-router";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import type { useTheme } from "@/providers/theme-provider";

interface AppShortcutsOptions {
  theme: ReturnType<typeof useTheme>;
  shortcutsOpen: () => boolean;
  setShortcutsOpen: (open: boolean) => void;
  setIsSidebarOpen: (f: (prev: boolean) => boolean) => void;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  const navigate = useNavigate();

  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });

  // ── Navigation ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.GOTO_UPCOMING, () => {
    void navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_ASSETS, () => {
    void navigate({ to: "/assets" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void navigate({ to: "/language" });
  });
}
